import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getSettingsApi, updateSettingsApi } from '../../api/adminApi';

const TOGGLE_SETTINGS = [
  { key: 'maintenance_mode',        label: 'Maintenance Mode',       desc: 'Show a maintenance page to all visitors' },
  { key: 'coupons_enabled',         label: 'Coupon Codes',           desc: 'Allow customers to apply discount codes at checkout' },
  { key: 'cod_enabled',             label: 'Cash on Delivery',       desc: 'Show COD as a payment option at checkout' },
  { key: 'loyalty_points_enabled',  label: 'Loyalty Points',         desc: 'Let customers earn and redeem loyalty points' },
  { key: 'reviews_enabled',         label: 'Product Reviews',        desc: 'Allow customers to write product reviews' },
];

function Toggle({ checked, onChange }) {
  return (
    <button
      style={{ ...S.toggle, background: checked ? 'var(--red)' : '#D1D5DB' }}
      onClick={() => onChange(!checked)}
      title={checked ? 'Enabled' : 'Disabled'}
    >
      <div style={{ ...S.toggleKnob, transform: checked ? 'translateX(20px)' : 'translateX(2px)' }} />
    </button>
  );
}

export default function SettingsTab() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [dirty, setDirty]       = useState(false);

  useEffect(() => {
    getSettingsApi()
      .then((r) => setSettings(r.data))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => { setSettings((prev) => ({ ...prev, [key]: val })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettingsApi(settings);
      toast.success('Settings saved successfully');
      setDirty(false);
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={S.loading}><div style={S.spinner} /></div>;

  return (
    <div>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Settings</h1>
          <p style={S.sub}>Store configuration and feature flags</p>
        </div>
        <button style={{ ...S.btnSave, opacity: dirty ? 1 : .5, cursor: dirty ? 'pointer' : 'default' }}
          onClick={dirty ? handleSave : undefined} disabled={saving || !dirty}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Store Configuration */}
      <div style={S.section}>
        <h2 style={S.sectionTitle}>Store Configuration</h2>
        <div style={S.formGrid}>
          <div style={S.field}>
            <label style={S.label}>Store Name</label>
            <input style={S.input} value={settings.store_name || ''} onChange={(e) => set('store_name', e.target.value)} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Support Email</label>
            <input style={S.input} type="email" value={settings.store_email || ''} onChange={(e) => set('store_email', e.target.value)} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Support Phone</label>
            <input style={S.input} value={settings.store_phone || ''} onChange={(e) => set('store_phone', e.target.value)} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Currency</label>
            <select style={S.input} value={settings.currency || 'INR'} onChange={(e) => set('currency', e.target.value)}>
              <option value="INR">INR — Indian Rupee (₹)</option>
              <option value="USD">USD — US Dollar ($)</option>
              <option value="EUR">EUR — Euro (€)</option>
            </select>
          </div>
          <div style={S.field}>
            <label style={S.label}>GST Number</label>
            <input style={S.input} value={settings.gst_number || ''} onChange={(e) => set('gst_number', e.target.value)} placeholder="e.g. 19AABCU9603R1ZV" />
          </div>
          <div style={S.field}>
            <label style={S.label}>Free Shipping Threshold (₹)</label>
            <input style={S.input} type="number" min="0" value={settings.free_shipping_threshold || 0} onChange={(e) => set('free_shipping_threshold', parseFloat(e.target.value) || 0)} />
          </div>
        </div>
        <div style={S.field}>
          <label style={S.label}>Store Address</label>
          <textarea style={{ ...S.input, resize: 'vertical', minHeight: '64px' }} value={settings.address || ''} onChange={(e) => set('address', e.target.value)} />
        </div>
      </div>

      {/* Feature Toggles */}
      <div style={S.section}>
        <h2 style={S.sectionTitle}>Feature Flags</h2>
        <div style={S.toggleList}>
          {TOGGLE_SETTINGS.map(({ key, label, desc }) => (
            <div key={key} style={S.toggleRow}>
              <div>
                <p style={S.toggleLabel}>{label}</p>
                <p style={S.toggleDesc}>{desc}</p>
              </div>
              <Toggle checked={!!settings[key]} onChange={(val) => set(key, val)} />
            </div>
          ))}
        </div>
      </div>

      {/* Danger Zone */}
      <div style={{ ...S.section, borderColor: '#FCA5A5' }}>
        <h2 style={{ ...S.sectionTitle, color: '#DC2626' }}>Danger Zone</h2>
        <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>
          These actions are destructive and cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={S.dangerBtn} onClick={() => toast.info('Contact your developer to flush cache')}>Flush Cache</button>
          <button style={S.dangerBtn} onClick={() => toast.info('Rebuild feature disabled in this environment')}>Rebuild Index</button>
        </div>
      </div>

      {/* Floating save bar */}
      {dirty && (
        <div style={S.saveBar}>
          <span style={{ fontSize: '13px', color: '#374151' }}>You have unsaved changes</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={S.btnDiscard} onClick={() => { setDirty(false); }}>Discard</button>
            <button style={S.btnSave} onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  loading:     { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' },
  spinner:     { width: '36px', height: '36px', border: '3px solid #E5E5E5', borderTopColor: 'var(--red)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  hdr:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' },
  title:       { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1.5px', color: '#111', margin: 0 },
  sub:         { fontSize: '13px', color: '#9CA3AF', marginTop: '2px' },
  btnSave:     { background: '#111', color: '#fff', border: 'none', padding: '10px 22px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '.5px' },
  section:     { background: '#fff', border: '1px solid #E5E5E5', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,.04)' },
  sectionTitle:{ fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '2px', color: '#374151', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #F3F4F6' },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
  field:       { display: 'flex', flexDirection: 'column', gap: '5px' },
  label:       { fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#6B7280' },
  input:       { border: '1.5px solid #E5E5E5', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  toggleList:  { display: 'flex', flexDirection: 'column', gap: '0' },
  toggleRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #F3F4F6' },
  toggleLabel: { fontSize: '14px', fontWeight: 700, color: '#111', margin: 0, marginBottom: '2px' },
  toggleDesc:  { fontSize: '12px', color: '#6B7280', margin: 0 },
  toggle:      { width: '44px', height: '24px', borderRadius: '99px', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background .2s', flexShrink: 0 },
  toggleKnob:  { position: 'absolute', top: '2px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'transform .2s' },
  dangerBtn:   { background: '#fff', border: '1.5px solid #FCA5A5', color: '#DC2626', padding: '8px 16px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' },
  saveBar:     { position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: '#fff', border: '1px solid #E5E5E5', boxShadow: '0 4px 24px rgba(0,0,0,.12)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '20px', zIndex: 999 },
  btnDiscard:  { background: '#fff', border: '1.5px solid #E5E5E5', padding: '8px 16px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', color: '#666' },
};
